import { Clim81Station, Forecast, ForecastDataPoint } from "../types";
import { bestWindow, CONDITIONS_THRESHOLDS, Conditions, ConditionsFactor, ConditionsRating, ConditionsScore, forecastDayConditions, parseNumber, rateForecastHour } from "./conditions";

/// Turns a forecast into the words and summaries shown on the area page.

const DAY = 86400;

// Hours considered for a climbing window, local time
const WINDOW_FIRST_HOUR = 7;
const WINDOW_LAST_HOUR = 18;

// Monthly average high range that counts as climbing season
export const SEASON_HIGH_MIN = 50;
export const SEASON_HIGH_MAX = 82;

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

interface LocalTime {
    date: string; // YYYY-MM-DD in the area's time zone
    hour: number; // 0–23
    weekday: string;
    weekdayShort: string;
    monthDay: string; // "Oct 30"
}

export function localTime(time: number, timeZone: string): LocalTime {
    const d = new Date(time * 1000);
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "numeric", hourCycle: "h23",
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    return {
        date: `${get("year")}-${get("month")}-${get("day")}`,
        hour: parseInt(get("hour"), 10) % 24,
        weekday: d.toLocaleString("en-US", { timeZone, weekday: "long" }),
        weekdayShort: d.toLocaleString("en-US", { timeZone, weekday: "short" }),
        monthDay: d.toLocaleString("en-US", { timeZone, month: "short", day: "numeric" }),
    };
}

/// "9 AM"
export function hourLabel(time: number, timeZone: string): string {
    return new Date(time * 1000).toLocaleString("en-US", { timeZone, hour: "numeric", hour12: true });
}

/// "Today", "Tomorrow" or the weekday name. The short form skips "Tomorrow"
/// ("Today", "Sat", "Sun") so it fits narrow columns.
export function dayLabel(time: number, timeZone: string, now: number, short = false): string {
    const date = localTime(time, timeZone);
    if (date.date === localTime(now, timeZone).date) {
        return "Today";
    }
    if (short) {
        return date.weekdayShort;
    }
    if (date.date === localTime(now + DAY, timeZone).date) {
        return "Tomorrow";
    }
    return date.weekday;
}

const FACTOR_WORDS: Record<ConditionsFactor, string> = {
    wet: "wet rock",
    damp: "damp rock early",
    rain: "rain possible",
    cold: "cold",
    hot: "hot",
    wind: "windy",
    muggy: "muggy",
};

const FACTOR_WORDS_MILD: Record<ConditionsFactor, string> = {
    wet: "wet rock",
    damp: "rock may be damp early",
    rain: "a chance of showers",
    cold: "chilly",
    hot: "warm",
    wind: "breezy",
    muggy: "a bit muggy",
};

function joinWords(words: string[]): string {
    if (words.length <= 1) {
        return words.join("");
    }
    return words.slice(0, -1).join(", ") + " and " + words[words.length - 1];
}

function capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
}

/// One plain sentence about the day's conditions
export function verdictLine(rating: ConditionsRating): string {
    const words = rating.factors.map((f) => FACTOR_WORDS[f]);
    switch (rating.score) {
        case 5:
            return "Prime conditions. Get out there.";
        case 4:
            return rating.factors.length > 0
                ? `Good day to climb, ${FACTOR_WORDS_MILD[rating.factors[0]]}.`
                : "Good day to climb.";
        case 3:
            return `Fair: ${joinWords(words)}. Pick your window.`;
        case 2:
            return `Iffy: ${joinWords(words)}. Check the hourly before you go.`;
        case 1:
            return rating.factors[0] === "wet"
                ? "Skip it. Expect wet rock."
                : `Skip it: ${joinWords(words)}.`;
    }
}

/// Short note for a day in the week list
export function dayNote(rating: ConditionsRating): string {
    if (rating.factors.length === 0) {
        return "Dry and comfortable";
    }
    const words = rating.factors.slice(0, 2).map((f) =>
        f === "rain" && rating.score <= 2 ? "rain likely" : FACTOR_WORDS[f]);
    return capitalize(words.join(", "));
}

export type HighlightKind = "window" | "noWindow" | "rain";

export interface Highlight {
    kind: HighlightKind;
    lead: string; // emphasized part
    text: string;
    score?: ConditionsScore;
}

/// Group consecutive hourly points by local date, keeping order
function hoursByDate(hours: ForecastDataPoint[], timeZone: string): ForecastDataPoint[][] {
    const groups: ForecastDataPoint[][] = [];
    let current = "";
    for (const dp of hours) {
        const date = localTime(dp.time, timeZone).date;
        if (date !== current) {
            groups.push([]);
            current = date;
        }
        groups[groups.length - 1].push(dp);
    }
    return groups;
}

const SCORE_WORDS: Record<ConditionsScore, string> = {
    5: "Prime conditions",
    4: "Good conditions",
    3: "Fair conditions",
    2: "Iffy conditions",
    1: "Poor conditions",
};

/// The best climbing window in the next two days, and the next rainy day
export function forecastHighlights(forecast: Forecast, now: number): Highlight[] {
    const tz = forecast.timezone;
    const highlights: Highlight[] = [];

    const days = hoursByDate(forecast.hourly?.data ?? [], tz).slice(0, 2);
    let window = null;
    for (const day of days) {
        const daytime = day.filter((dp) => {
            const hour = localTime(dp.time, tz).hour;
            return hour >= WINDOW_FIRST_HOUR && hour <= WINDOW_LAST_HOUR;
        });
        window = bestWindow(daytime, { rate: (dp) => rateForecastHour(forecast, dp) });
        if (window) {
            break;
        }
    }
    if (window) {
        highlights.push({
            kind: "window",
            lead: `Best window: ${dayLabel(window.start, tz, now)} ${hourLabel(window.start, tz)} – ${hourLabel(window.end, tz)}.`,
            text: `${SCORE_WORDS[window.score]}.`,
            score: window.score,
        });
    } else if (days.length > 0) {
        highlights.push({
            kind: "noWindow",
            lead: "No good climbing window in the next two days.",
            text: "Check the week ahead for a better day.",
        });
    }

    // The first rainy daytime or night, so "Rain likely tonight" isn't hidden behind a dry day
    const rainy = findRain(forecast, now);
    if (rainy) {
        const chance = Math.round(rainy.chance * 100);
        // Same "likely" cutoff as the week list, which says "Rain likely" only once the
        // chance alone drops a day to Iffy
        const likely = chance >= 60;
        highlights.push({
            kind: "rain",
            lead: `${likely ? "Rain likely" : "Chance of rain"} ${rainy.when}.`,
            text: [
                `${chance}%`,
                rainy.amount > 0 ? `, about ${rainy.amount.toFixed(2)}"` : "",
                rainy.amount >= CONDITIONS_THRESHOLDS.precipAccumulationWet ? ". Give the rock time to dry." : ".",
            ].join(""),
        });
    }

    return highlights;
}

// A daytime or night counts as rainy at this chance or amount
const RAINY_CHANCE = 0.5;
const RAINY_AMOUNT = 0.1;

function findRain(forecast: Forecast, now: number): { when: string; chance: number; amount: number } | null {
    const tz = forecast.timezone;
    const days = forecast.daily?.data ?? [];
    for (let i = 0; i < days.length; i++) {
        const c = forecastDayConditions(forecast, i);
        const label = dayLabel(days[i].time, tz, now);
        const dayChance = c.precipProbability ?? 0;
        const dayAmount = c.precipAccumulation ?? 0;
        if (dayChance >= RAINY_CHANCE || dayAmount >= RAINY_AMOUNT) {
            return { when: label, chance: dayChance, amount: dayAmount };
        }
        const nightChance = c.precipProbabilityNight ?? 0;
        const nightAmount = c.precipAccumulationNight ?? 0;
        if (nightChance >= RAINY_CHANCE || nightAmount >= RAINY_AMOUNT) {
            return { when: label === "Today" ? "tonight" : `${label} night`, chance: nightChance, amount: nightAmount };
        }
    }
    return null;
}

/// Compact rain line for a day in the week list: "40% · 0.20\"", or "1% · 52% night · 0.06\""
/// when the night differs. Null when there's nothing worth showing.
export function dayRainText(c: Conditions): string | null {
    const percent = (p: number) => `${Math.round(p * 100)}%`;
    const inches = (amount: number | undefined) => ((amount ?? 0) >= 0.01 ? ` · ${amount!.toFixed(2)}"` : "");
    const day = c.precipProbability ?? 0;
    const night = c.precipProbabilityNight;
    const showDay = day >= 0.2 || (c.precipAccumulation ?? 0) >= 0.01;
    const showNight = night !== undefined && (night >= 0.2 || (c.precipAccumulationNight ?? 0) >= 0.01);
    if (!showDay && !showNight) {
        return null;
    }
    const parts = [percent(day) + inches(c.precipAccumulation)];
    if (showNight) {
        parts.push(`${percent(night!)} night` + inches(c.precipAccumulationNight));
    }
    return parts.join(" · ");
}

export interface SeasonMonth {
    name: string;
    high?: number;
    precip?: number;
    inSeason: boolean;
}

/// Monthly averages from the climate station; null when it has no monthly data
export function seasonMonths(station: Clim81Station | null | undefined): SeasonMonth[] | null {
    // The API sends these as strings despite the number[] type
    const high = station?.high as (number | string)[] | null | undefined;
    const precip = station?.precip as (number | string)[] | null | undefined;
    if (!high && !precip) {
        return null;
    }
    return MONTHS.map((name, i) => {
        const h = parseNumber(high?.[i]);
        return {
            name,
            high: h,
            precip: parseNumber(precip?.[i]),
            inSeason: h !== undefined && h >= SEASON_HIGH_MIN && h <= SEASON_HIGH_MAX,
        };
    });
}

function titleCase(text: string): string {
    return text.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/// "Averages from Lake Placid 2 S, 3 miles away and 120 feet lower than the area."
export function stationDescription(station: Clim81Station, distance?: number | null, areaElevation?: number | null): string {
    const name = station.stationName ? titleCase(station.stationName) : "the nearest weather station";
    const details: string[] = [];
    if (distance) {
        details.push(`${distance} ${distance === 1 ? "mile" : "miles"} away`);
    }
    const stationElevation = parseNumber(station.elevation);
    if (areaElevation && stationElevation !== undefined && areaElevation !== stationElevation) {
        const diff = Math.round(Math.abs(areaElevation - stationElevation));
        const direction = stationElevation > areaElevation ? "higher" : "lower";
        details.push(`${diff.toLocaleString("en-US")} ${diff === 1 ? "foot" : "feet"} ${direction} than the area`);
    }
    return `Averages from ${name}${details.length > 0 ? ", " + joinWords(details) : ""}.`;
}
